import { isDictionary } from "@/helpers/is-dictionary";
import { isValidKey } from "@/helpers/is-valid-key";
import { cloneDeep } from "@/functions/clone-deep";
import { isObject, type ReferenceType } from "@/helpers/is-object";

/**
 * A simple and straightforward deep merge function that recursively merges objects.
 * Handles cyclic references to prevent infinite recursion.
 *
 * @param target - The target object that will receive the merged values
 * @param sources - One or more source objects to merge into the target
 * @param visited - WeakMap tracking already processed objects to handle cyclic references
 *
 * @returns A new merged object without modifying any of the inputs
 */
export function deepMerge<T extends ReferenceType>(
  target: T,
  ...sources: Array<any>
): T {
  return deepMergeInternal(target, sources, new WeakMap());
}

/**
 * Internal implementation of deepMerge that tracks visited objects
 */
function deepMergeInternal<T extends ReferenceType>(
  target: T,
  sources: Array<any>,
  visited: WeakMap<object, any>
): T {
  if (!sources.length || sources.every((source) => !source)) {
    return cloneDeep(target, visited);
  }

  // If target has been visited before, return the cached result
  if (isObject(target) && visited.has(target)) {
    return visited.get(target);
  }

  const result = cloneDeep(target, visited);

  if (isObject(target)) visited.set(target, result);

  for (const source of sources) {
    if (!source) continue;

    for (const key of Reflect.ownKeys(source)) {
      if (!isValidKey(key)) continue;

      const targetValue = result[key];
      const sourceValue = Reflect.get(source, key);

      if (sourceValue === undefined) continue;

      if (isObject(sourceValue) && visited.has(sourceValue)) {
        Reflect.set(result, key, visited.get(sourceValue));
        continue;
      }

      if (isDictionary(targetValue) && isDictionary(sourceValue)) {
        Reflect.set(
          result,
          key,
          deepMergeInternal(targetValue, [sourceValue], visited)
        );
        continue;
      }

      Reflect.set(result, key, cloneDeep(sourceValue, visited));
    }
  }

  return result as T;
}
